import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import Table from '../Tables/Table';
import styles from './EducatorDayOfWeek.module.scss';
import generalStyles from './SchoolReport.module.scss';
import { ReportingMixpanelEvent } from '@peekapak/types/ReportDataExchangeTypes';
import { lookupNewDBStartDate } from '../reportUtilities';

interface Props {
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EducatorDayOfWeek = ({
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
}: Props): JSX.Element => {
  const [data, setData] = useState<{ day: string; count: number }[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (
      legacyPlatformEventsReady &&
      legacyPeekavilleEventsReady &&
      newMixpanelEventsReady
    ) {
      buildData();
    }
  }, [
    legacyPlatformEvents,
    legacyPlatformEventsReady,
    legacyPeekavilleEvents,
    legacyPeekavilleEventsReady,
    newMixpanelEvents,
    newMixpanelEventsReady,
  ]);

  function buildData() {
    const allStaffEvents = [
      ...legacyPlatformEvents,
      ...newMixpanelEvents,
    ];

    const educatorLoginEvents = allStaffEvents.filter((event) => {
      const isStaff = ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(event.type);
      if (!isStaff) return false;
      const isNewLogin = event.name === 'login';
      const isLegacy = new Date(event.time) <= lookupNewDBStartDate('peekapakLogin') &&
        (event.name === 'User Login' || event.name === 'login' || event.name === 'User Reactivated');
      return isNewLogin || isLegacy;
    });

    const dayCounts = Array(7).fill(0);
    educatorLoginEvents.forEach((event) => {
      const day = new Date(event.time).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
      const dayIndex = DAY_NAMES.indexOf(day);
      if (dayIndex >= 0) dayCounts[dayIndex]++;
    });

    const result = dayCounts
      .map((count, i) => ({ day: DAY_NAMES[i], count }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);

    setData(result);
    setReady(true);
  }

  return (
    <div className={styles.educatorDayOfWeek}>
      <h4 className={generalStyles.tableHeader}>Day of Week Educators Log In</h4>
      <Table tableHeader={[{ title: 'Day' }, { title: 'Logins' }]}>
        {getContent()}
      </Table>
    </div>
  );

  function getContent() {
    if (!ready) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>
              Loading
              <Spinner name='line-scale' />
            </div>
          </td>
        </tr>
      );
    }

    if (data.length === 0) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>No data available</div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {data.map((d) => (
          <tr key={d.day}>
            <td>{d.day}</td>
            <td>{d.count}</td>
          </tr>
        ))}
      </>
    );
  }
};

export default EducatorDayOfWeek;
